/*!
 * msk-corruption.js — MSK Phase 2 · Unit P2-B · the corruption grammar
 * --------------------------------------------------------------------------
 * "The Emblemata image is the light. The glitch is where the dark matter
 *  is pulling it apart." Corruption is damage to the source file, never a
 *  stylistic filter. Rare. Quiet. Same ink.
 *
 * THE CORRUPTION API (contract — Units A/C consume this verbatim):
 *
 *   data-msk-decay          on first viewport entry, text scrambles from
 *                           engraving-noise glyphs (.·:;+*#%@§†‡) and
 *                           resolves to the real text over ~0.9–1.4s,
 *                           char by char, left to right, randomized settle.
 *                           Fires once. Optional data-msk-decay-delay="ms".
 *                           Real text sits in the DOM until the effect runs.
 *   data-msk-strike         rare (randomized 8–20s, at most once/interval,
 *                           only while in viewport) 1–2 frame "struck twice"
 *                           duplicate: clip-path top/bottom slices offset
 *                           2–6px horizontally, same color. Also fires on
 *                           mouseenter (max once per 4s).
 *   data-msk-reveal         surfaces from the dark-matter layer on viewport
 *                           entry (opacity ~0.15 / border --msk-absolute ->
 *                           final over ~1.2s ease-out). Sibling stagger via
 *                           data-msk-reveal-order="n" (n × 120ms).
 *   data-msk-scanline       hover/focus: one horizontal band (10–18% height,
 *                           random y) of the contained img displaces 4–8px
 *                           for 80–140ms, once per hover. Clip + duplicate
 *                           layer — no filters, no color shift.
 *
 * JS surface:  window.MSKCorruption = { scan(root=document), version }
 *              scan() is idempotent; wired to shopify:section:load/unload.
 * On init the class `msk-corrupt-ready` is added to <html> — ALSO under
 * prefers-reduced-motion, where every effect no-ops and final states are
 * applied immediately. Consumers may only apply content-hiding resting
 * states scoped under .msk-corrupt-ready (the no-JS safety valve).
 *
 * One shared rAF scheduler drives everything (no per-element intervals);
 * it self-stops when idle, pauses when the tab is hidden, and one-shot
 * IntersectionObservers are disconnected once drained.
 *
 * No dependencies. No new colors. No filters. Vanilla, Dawn-safe.
 */
(function () {
  'use strict';

  if (window.MSKCorruption) return; // idempotent load

  var VERSION = '1.0.0';

  /* ------------------------------------------------------------------ *
   *  Tunables — all values sit inside the contract ranges.
   * ------------------------------------------------------------------ */
  // Engraving damage, never Matrix rain: .·:;+*#%@§†‡ + stray period-plausibles.
  var GLYPHS = '.·:;+*#%@§†‡,\'';

  var DECAY_MIN_MS = 900;       // contract: ~0.9–1.4s
  var DECAY_MAX_MS = 1400;
  var DECAY_SHUFFLE_MS = 48;    // glyph re-randomization cadence (~20fps — reads more like damage, costs less)
  var DECAY_RAMP = 0.72;        // share of duration spent on the left→right ramp; rest is per-char jitter

  var STRIKE_MIN_MS = 8000;     // contract: 8–20s ambient interval
  var STRIKE_MAX_MS = 20000;
  var STRIKE_FRAMES = 2;        // contract: 1–2 frame flash
  var STRIKE_HOVER_COOLDOWN_MS = 4000;
  var STRIKE_SWEEP_MS = 250;    // ambient schedule check cadence inside the shared loop
  var STRIKE_X_MIN = 2;         // contract: 2–6px offset
  var STRIKE_X_MAX = 6;
  var STRIKE_CUT_MIN = 32;      // % — randomized slice boundary between the two duplicates
  var STRIKE_CUT_MAX = 66;

  var REVEAL_STAGGER_MS = 120;  // contract: order × 120ms

  var SCANLINE_H_MIN = 10;      // contract: 10–18% band height
  var SCANLINE_H_MAX = 18;
  var SCANLINE_DX_MIN = 4;      // contract: 4–8px displacement
  var SCANLINE_DX_MAX = 8;
  var SCANLINE_MS_MIN = 80;     // contract: 80–140ms
  var SCANLINE_MS_MAX = 140;

  var SELECTOR = '[data-msk-decay],[data-msk-strike],[data-msk-reveal],[data-msk-scanline]';

  /* ------------------------------------------------------------------ *
   *  Utilities
   * ------------------------------------------------------------------ */
  var docEl = document.documentElement;
  var reduceMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function reduced() { return !!(reduceMq && reduceMq.matches); }
  function rand(min, max) { return min + Math.random() * (max - min); }
  function randGlyph() { return GLYPHS.charAt((Math.random() * GLYPHS.length) | 0); }
  function now() { return performance.now(); }
  function intAttr(el, name) {
    var v = parseInt(el.getAttribute(name), 10);
    return v > 0 ? v : 0;
  }
  function ensurePositioned(el) {
    if (window.getComputedStyle(el).position === 'static') el.style.position = 'relative';
  }

  /* ------------------------------------------------------------------ *
   *  State
   * ------------------------------------------------------------------ */
  var perEl = new WeakMap();   // element -> { decay?, strike?, reveal?, scanline?, oneShotQueued? }
  var records = new Set();     // every live record (teardown / reduced-motion finalize)
  var strikeRecs = new Set();  // strike records only (ambient sweep)

  function flagsFor(el) {
    var f = perEl.get(el);
    if (!f) { f = {}; perEl.set(el, f); }
    return f;
  }

  /* ------------------------------------------------------------------ *
   *  Shared scheduler — ONE rAF loop for the whole library.
   *  Tasks are { run(ts) -> boolean keep, dead? }. The loop self-stops
   *  when the task set drains and pauses while the tab is hidden.
   * ------------------------------------------------------------------ */
  var tasks = new Set();
  var rafId = null;

  function frame(ts) {
    rafId = null;
    tasks.forEach(function (t) {
      var keep = false;
      try {
        keep = !t.dead && t.run(ts) !== false;
      } catch (err) {
        if (window.console && console.error) console.error('[msk-corruption]', err);
      }
      if (!keep) tasks.delete(t);
    });
    armScheduler();
  }

  function armScheduler() {
    if (rafId === null && tasks.size > 0 && !document.hidden) {
      rafId = window.requestAnimationFrame(frame);
    }
  }

  function addTask(t) {
    tasks.add(t);
    armScheduler();
    return t;
  }

  /* ------------------------------------------------------------------ *
   *  One-shot IntersectionObserver (decay + reveal share it).
   *  Disconnected + released once every one-shot has fired.
   * ------------------------------------------------------------------ */
  var oneShotIO = null;
  var oneShotPending = 0;

  function queueOneShot(el) {
    var f = flagsFor(el);
    if (f.oneShotQueued) return;
    if (!('IntersectionObserver' in window)) { fireOneShots(el); return; }
    f.oneShotQueued = true;
    if (!oneShotIO) {
      oneShotIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          releaseOneShot(entry.target);
          fireOneShots(entry.target);
        });
      }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });
    }
    oneShotPending += 1;
    oneShotIO.observe(el);
  }

  function releaseOneShot(el) {
    var f = perEl.get(el);
    if (!f || !f.oneShotQueued) return;
    f.oneShotQueued = false;
    if (oneShotIO) {
      oneShotIO.unobserve(el);
      oneShotPending -= 1;
      if (oneShotPending <= 0) {
        oneShotIO.disconnect();
        oneShotIO = null;
        oneShotPending = 0;
      }
    }
  }

  function fireOneShots(el) {
    var f = perEl.get(el);
    if (!f) return;
    if (f.decay) activateDecay(f.decay);
    if (f.reveal) activateReveal(f.reveal);
  }

  /* ------------------------------------------------------------------ *
   *  data-msk-decay — resolves out of engraving noise. Once.
   * ------------------------------------------------------------------ */
  function bindDecay(el) {
    var f = flagsFor(el);
    if (f.decay) return;
    var r = { type: 'decay', el: el, done: false, active: false, restore: null };
    f.decay = r;
    records.add(r);
    if (reduced()) { r.done = true; return; } // final (real) text already in the DOM
    queueOneShot(el);
  }

  function activateDecay(r) {
    if (r.done || r.active) return;
    if (reduced() || !r.el.isConnected) { r.done = true; return; }
    var el = r.el;

    // Collect text nodes (preserves nested markup — only nodeValues mutate).
    var nodes = [];
    (function collect(n) {
      for (var c = n.firstChild; c; c = c.nextSibling) {
        if (c.nodeType === 3) { if (c.nodeValue) nodes.push({ node: c, text: c.nodeValue }); }
        else if (c.nodeType === 1) collect(c);
      }
    })(el);

    var total = 0;
    var i;
    for (i = 0; i < nodes.length; i++) total += nodes[i].text.length;
    if (!total) { r.done = true; return; }

    r.active = true;
    var duration = rand(DECAY_MIN_MS, DECAY_MAX_MS);
    var delay = intAttr(el, 'data-msk-decay-delay');

    // Per-char settle times: left→right ramp + randomized jitter, capped at duration.
    var idx = 0;
    nodes.forEach(function (n) {
      n.settle = [];
      for (var k = 0; k < n.text.length; k++) {
        if (/\s/.test(n.text.charAt(k))) {
          n.settle[k] = -1; // whitespace never scrambles (layout keeps its skeleton)
        } else {
          n.settle[k] = Math.min(
            duration,
            (idx / total) * duration * DECAY_RAMP + rand(0, duration * (1 - DECAY_RAMP))
          );
        }
        idx += 1;
      }
    });

    r.restore = function () {
      nodes.forEach(function (n) { n.node.nodeValue = n.text; });
      el.classList.remove('msk-decay--active');
      r.active = false;
      r.done = true;
    };

    var start = -1;
    var started = false;
    var lastShuffle = -1e9;

    addTask({
      run: function (ts) {
        if (r.done) return false; // finalized externally (reduced-motion flip / teardown)
        if (start < 0) start = ts + delay;
        if (ts < start) return true; // waiting out data-msk-decay-delay
        if (!started) { started = true; el.classList.add('msk-decay--active'); }
        var t = ts - start;
        if (t >= duration) { r.restore(); return false; }
        if (ts - lastShuffle < DECAY_SHUFFLE_MS) return true;
        lastShuffle = ts;
        nodes.forEach(function (n) {
          var out = '';
          for (var k = 0; k < n.text.length; k++) {
            var s = n.settle[k];
            out += (s < 0 || t >= s) ? n.text.charAt(k) : randGlyph();
          }
          n.node.nodeValue = out;
        });
        return true;
      }
    });
  }

  /* ------------------------------------------------------------------ *
   *  data-msk-strike — the plate struck twice. Rare. 1–2 frames.
   * ------------------------------------------------------------------ */
  var strikeIO = null;
  var strikeTask = null;

  function bindStrike(el) {
    var f = flagsFor(el);
    if (f.strike) return;
    var r = {
      type: 'strike', el: el,
      inView: false, nextAt: 0, lastHover: -Infinity, hitTask: null
    };
    r.onHover = function () {
      var t = now();
      if (t - r.lastHover < STRIKE_HOVER_COOLDOWN_MS) return;
      r.lastHover = t;
      fireStrike(r, t);
    };
    el.addEventListener('mouseenter', r.onHover);
    ensurePositioned(el);
    f.strike = r;
    records.add(r);
    strikeRecs.add(r);
    observeStrike(el);
  }

  function observeStrike(el) {
    if (!('IntersectionObserver' in window)) {
      var f = perEl.get(el);
      if (f && f.strike) {
        f.strike.inView = true;
        f.strike.nextAt = now() + rand(STRIKE_MIN_MS, STRIKE_MAX_MS);
      }
      ensureStrikeTask();
      return;
    }
    if (!strikeIO) {
      strikeIO = new IntersectionObserver(function (entries) {
        var t = now();
        entries.forEach(function (entry) {
          var f = perEl.get(entry.target);
          var r = f && f.strike;
          if (!r) return;
          r.inView = entry.isIntersecting;
          // Never fire the instant something scrolls in — always a fresh 8–20s fuse.
          if (r.inView && (!r.nextAt || r.nextAt < t)) {
            r.nextAt = t + rand(STRIKE_MIN_MS, STRIKE_MAX_MS);
          }
        });
        ensureStrikeTask();
      }, { threshold: 0 });
    }
    strikeIO.observe(el);
  }

  function ensureStrikeTask() {
    if (strikeTask || reduced()) return;
    var anyVisible = false;
    strikeRecs.forEach(function (r) { if (r.inView) anyVisible = true; });
    if (!anyVisible) return;
    /* perf 2026-10-13: the ambient sweep only needs to look 4x a second, so it runs on a
       timer. The old version sat in the shared rAF loop, which made the browser produce a
       full frame every 16ms for as long as one strike element was on screen. */
    var sweep = function () {
      var ts = now();
      var visible = false;
      if (!reduced() && !document.hidden) {
        strikeRecs.forEach(function (r) {
          if (!r.inView) return;
          visible = true;
          if (ts >= r.nextAt) fireStrike(r, ts);
        });
      }
      if (!visible && !document.hidden) { clearInterval(strikeTask.id); strikeTask = null; }
    };
    strikeTask = { id: setInterval(sweep, STRIKE_SWEEP_MS) };
  }

  function fireStrike(r, ts) {
    // Always re-arm the fuse, even when the hit itself is skipped.
    r.nextAt = ts + rand(STRIKE_MIN_MS, STRIKE_MAX_MS);
    if (reduced() || r.hitTask || !r.el.isConnected) return;
    var el = r.el;
    var txt = el.textContent || '';
    if (!txt.replace(/\s/g, '')) return; // nothing to duplicate
    el.setAttribute('data-msk-strike-text', txt);
    var x = (Math.random() < 0.5 ? -1 : 1) * rand(STRIKE_X_MIN, STRIKE_X_MAX);
    el.style.setProperty('--msk-strike-x', x.toFixed(1) + 'px');
    el.style.setProperty('--msk-strike-cut', rand(STRIKE_CUT_MIN, STRIKE_CUT_MAX).toFixed(1) + '%');
    el.classList.add('msk-strike--hit');
    var frames = 0;
    r.hitTask = addTask({
      run: function () {
        frames += 1;
        if (frames >= STRIKE_FRAMES) {
          el.classList.remove('msk-strike--hit');
          r.hitTask = null;
          return false;
        }
        return true;
      }
    });
  }

  /* ------------------------------------------------------------------ *
   *  data-msk-reveal — surfaces from the dark-matter layer. Once.
   * ------------------------------------------------------------------ */
  function bindReveal(el) {
    var f = flagsFor(el);
    if (f.reveal) return;
    var r = { type: 'reveal', el: el, done: false };
    f.reveal = r;
    records.add(r);
    if (reduced()) { finalizeRecord(r); return; } // final state immediately
    queueOneShot(el);
  }

  function activateReveal(r) {
    if (r.done) return;
    r.done = true;
    var el = r.el;
    var order = intAttr(el, 'data-msk-reveal-order');
    if (reduced() || !order) {
      el.classList.add('msk-reveal--surfaced');
      return;
    }
    var at = now() + order * REVEAL_STAGGER_MS;
    addTask({
      run: function (ts) {
        if (!el.isConnected) return false;
        if (ts < at) return true;
        el.classList.add('msk-reveal--surfaced');
        return false;
      }
    });
  }

  /* ------------------------------------------------------------------ *
   *  data-msk-scanline — one band of the image, displaced. Once per hover.
   * ------------------------------------------------------------------ */
  function bindScanline(el) {
    var f = flagsFor(el);
    if (f.scanline) return;
    var r = {
      type: 'scanline', el: el,
      armed: true, dupe: null, dupeImg: null, container: null, hitTask: null
    };
    r.onEnter = function () { fireScanline(r); };
    r.onLeave = function () { r.armed = true; };
    el.addEventListener('mouseenter', r.onEnter);
    el.addEventListener('mouseleave', r.onLeave);
    el.addEventListener('focusin', r.onEnter);
    el.addEventListener('focusout', r.onLeave);
    f.scanline = r;
    records.add(r);
  }

  function fireScanline(r) {
    if (reduced() || !r.armed || r.hitTask) return;
    var el = r.el;
    var img = el.tagName === 'IMG' ? el : el.querySelector('img');
    if (!img) return;
    var src = img.currentSrc || img.src;
    if (!src) return;
    var container = el.tagName === 'IMG' ? el.parentElement : el;
    if (!container) return;
    var ib = img.getBoundingClientRect();
    if (ib.width < 12 || ib.height < 12) return; // not laid out yet — stay armed

    r.armed = false;
    r.container = container;
    ensurePositioned(container);

    if (!r.dupe) {
      r.dupe = document.createElement('div');
      r.dupe.className = 'msk-scanline__dupe';
      r.dupe.setAttribute('aria-hidden', 'true');
      r.dupeImg = document.createElement('img');
      r.dupeImg.alt = '';
      r.dupeImg.draggable = false;
      r.dupe.appendChild(r.dupeImg);
    }
    if (r.dupe.parentNode !== container) container.appendChild(r.dupe);

    // Geometry at fire time (responsive-safe): image box relative to the
    // container's padding box (absolute containing block), border-corrected.
    var cb = container.getBoundingClientRect();
    var st = r.dupe.style;
    st.left = (ib.left - cb.left - container.clientLeft) + 'px';
    st.top = (ib.top - cb.top - container.clientTop) + 'px';
    st.width = ib.width + 'px';
    st.height = ib.height + 'px';

    if (r.dupeImg.getAttribute('src') !== src) r.dupeImg.src = src;
    var cs = window.getComputedStyle(img);
    r.dupeImg.style.objectFit = cs.objectFit || 'fill';
    r.dupeImg.style.objectPosition = cs.objectPosition || '50% 50%';

    var h = rand(SCANLINE_H_MIN, SCANLINE_H_MAX);
    var y = rand(0, 100 - h);
    var dx = (Math.random() < 0.5 ? -1 : 1) * rand(SCANLINE_DX_MIN, SCANLINE_DX_MAX);
    r.dupeImg.style.clipPath = 'inset(' + y.toFixed(2) + '% 0 ' + (100 - y - h).toFixed(2) + '% 0)';
    r.dupeImg.style.transform = 'translateX(' + dx.toFixed(1) + 'px)';

    container.classList.add('msk-scanline--hit');
    var end = now() + rand(SCANLINE_MS_MIN, SCANLINE_MS_MAX);
    r.hitTask = addTask({
      run: function (ts) {
        if (ts < end) return true;
        container.classList.remove('msk-scanline--hit');
        r.hitTask = null;
        return false;
      }
    });
  }

  /* ------------------------------------------------------------------ *
   *  Finalize (reduced-motion flip) + teardown (section unload)
   * ------------------------------------------------------------------ */
  function finalizeRecord(r) {
    if (r.type === 'decay') {
      if (r.active && r.restore) r.restore();
      r.done = true;
    } else if (r.type === 'reveal') {
      r.done = true;
      r.el.classList.add('msk-reveal--surfaced');
    } else if (r.type === 'strike') {
      if (r.hitTask) { r.hitTask.dead = true; r.hitTask = null; }
      r.el.classList.remove('msk-strike--hit');
    } else if (r.type === 'scanline') {
      if (r.hitTask) { r.hitTask.dead = true; r.hitTask = null; }
      if (r.container) r.container.classList.remove('msk-scanline--hit');
    }
  }

  function onReduceChange() {
    if (reduced()) {
      // Live flip to reduced motion: everything lands in its final state now.
      records.forEach(finalizeRecord);
      records.forEach(function (r) {
        if (r.type === 'decay' || r.type === 'reveal') releaseOneShot(r.el);
      });
      // Ambient strike task self-terminates on its next tick.
    } else {
      // Flip back: ambient effects resume; completed one-shots stay completed.
      strikeRecs.forEach(function (r) {
        if (r.inView) r.nextAt = now() + rand(STRIKE_MIN_MS, STRIKE_MAX_MS);
      });
      ensureStrikeTask();
    }
  }

  function teardown(root) {
    if (!root || root.nodeType !== 1) return;
    records.forEach(function (r) {
      if (!root.contains(r.el)) return;
      r.dead = true;
      finalizeRecord(r);
      if (r.type === 'strike') {
        r.el.removeEventListener('mouseenter', r.onHover);
        if (strikeIO) strikeIO.unobserve(r.el);
        strikeRecs.delete(r);
      } else if (r.type === 'scanline') {
        r.el.removeEventListener('mouseenter', r.onEnter);
        r.el.removeEventListener('mouseleave', r.onLeave);
        r.el.removeEventListener('focusin', r.onEnter);
        r.el.removeEventListener('focusout', r.onLeave);
        if (r.dupe && r.dupe.parentNode) r.dupe.parentNode.removeChild(r.dupe);
      } else {
        releaseOneShot(r.el);
      }
      records.delete(r);
      perEl.delete(r.el);
    });
    if (strikeIO && strikeRecs.size === 0) {
      strikeIO.disconnect();
      strikeIO = null;
    }
  }

  /* ------------------------------------------------------------------ *
   *  scan — the public entry point. Idempotent by construction.
   * ------------------------------------------------------------------ */
  function bind(el) {
    if (el.hasAttribute('data-msk-decay')) bindDecay(el);
    if (el.hasAttribute('data-msk-strike')) bindStrike(el);
    if (el.hasAttribute('data-msk-reveal')) bindReveal(el);
    if (el.hasAttribute('data-msk-scanline')) bindScanline(el);
  }

  function scan(root) {
    var scope = (root && (root.nodeType === 1 || root.nodeType === 9)) ? root : document;
    if (scope.nodeType === 1 && scope.matches && scope.matches(SELECTOR)) bind(scope);
    var list = scope.querySelectorAll(SELECTOR);
    for (var i = 0; i < list.length; i++) bind(list[i]);
  }

  /* ------------------------------------------------------------------ *
   *  Wiring
   * ------------------------------------------------------------------ */
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    } else {
      // Don't let strikes that "matured" while hidden all fire at once on return.
      var t = now();
      strikeRecs.forEach(function (r) {
        if (r.nextAt && r.nextAt < t) r.nextAt = t + rand(STRIKE_MIN_MS, STRIKE_MAX_MS);
      });
      armScheduler();
    }
  });

  if (reduceMq) {
    if (reduceMq.addEventListener) reduceMq.addEventListener('change', onReduceChange);
    else if (reduceMq.addListener) reduceMq.addListener(onReduceChange); // older Safari
  }

  // Shopify theme editor: rescan loaded sections, release unloaded ones.
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { teardown(e.target); });

  function init() {
    // Ready class FIRST (also under reduced motion — contract): consumers'
    // ready-scoped resting states may now apply; scan() then arms/finalizes.
    docEl.classList.add('msk-corrupt-ready');
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.MSKCorruption = { scan: scan, version: VERSION };
})();
